import FileDropzone from "../../Utility/FileDropzone/FileDropzone";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import { CLIENT_ATTACHMENT_ACCEPTED_TYPES } from "../../utils/fileUploadValidation";
import { RiCloseLine, RiDownloadLine, RiFileLine, RiLoader4Line } from "react-icons/ri";

/**
 * Attachments UI aligned with {@link ../Clients/EditClient.jsx}:
 * existing files as bordered rows with icon + download/delete; new files via {@link FileDropzone}.
 */
const FileUploadPanel = ({
  onFilesChange,
  maxFiles = 10,
  maxSizeMB = 10,
  existingAttachments = [],
  onDownloadExisting,
  downloadingAttachmentId = null,
  onDeleteExisting = null,
  deletingAttachmentId = null,
  readOnly = false,
  fileDropzoneKey = 0,
}) => {
  const acceptedTypes = `${CLIENT_ATTACHMENT_ACCEPTED_TYPES},image/*`;

  return (
    <div className="w-full mb-2">
      <Typography variant="text2" className="font-medium text-text-primary mb-2 block">
        Attachments
      </Typography>

      {existingAttachments.length > 0 ? (
        <ul className="space-y-2 mb-3">
          {existingAttachments.map((attachment) => {
            const attachmentId = attachment?.id;
            const idStr = attachmentId != null ? String(attachmentId) : "";
            const name =
              attachment?.originalName ??
              attachment?.fileName ??
              attachment?.name ??
              attachmentId ??
              "Attachment";
            const isBusy = downloadingAttachmentId === idStr || deletingAttachmentId === idStr;
            return (
              <li
                key={attachmentId ?? name}
                className="flex items-center justify-between p-2 bg-surface-secondary border border-border-primary rounded-md group"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <RiFileLine className="text-text-secondary shrink-0" />
                  <button
                    type="button"
                    onClick={() => onDownloadExisting?.(attachment)}
                    className="text-left min-w-0 flex-1 text-brand-primary hover:underline truncate"
                    disabled={!attachmentId || isBusy}
                  >
                    {name}
                  </button>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onDownloadExisting?.(attachment)}
                    className="shrink-0 !p-1"
                    title="Download"
                    disabled={!attachmentId || isBusy}
                  >
                    <RiDownloadLine className="w-4 h-4" />
                  </Button>
                  {typeof onDeleteExisting === "function" ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onDeleteExisting(attachment)}
                      className="shrink-0 !p-1"
                      title={deletingAttachmentId === idStr ? "Deleting…" : "Delete"}
                      disabled={!attachmentId || isBusy}
                    >
                      {deletingAttachmentId === idStr ? (
                        <RiLoader4Line className="w-4 h-4 animate-spin" aria-hidden />
                      ) : (
                        <RiCloseLine className="w-4 h-4" aria-hidden />
                      )}
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-sm text-text-secondary py-2 mb-2">No attachments yet.</div>
      )}

      {!readOnly && (
        <FileDropzone
          key={fileDropzoneKey}
          label="Add attachments (max 10 files, 10MB each)"
          onFilesChange={onFilesChange}
          maxFiles={maxFiles}
          maxSizeMB={maxSizeMB}
          acceptedTypes={acceptedTypes}
          inspectContent={false}
        />
      )}
    </div>
  );
};

export default FileUploadPanel;
